import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const FinancialServices = () => {
  return (
    <>
      <SEO 
        title="Financial Services Solutions - Banking & Insurance AI Technology"
        description="Transform financial services with AI-powered solutions for banking, insurance, wealth management, and fintech. Enhance security, compliance, and customer experience with advanced technology."
        keywords="financial services AI, banking technology, insurance software, fintech solutions, regulatory compliance, fraud detection, wealth management technology"
      />

      {/* Hero Section */}
      <section className="relative pt-10 md:pt-10 pb-10 bg-gradient-to-br from-pearl-white via-white to-blush-light/10 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #E63946 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }}></div>
        </div>

        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Breadcrumb */}
            <div className="flex items-center justify-center space-x-2 text-sm text-titanium mb-6 animate-fade-in">
              <Link to="/" className="hover:text-primary-red transition-colors">Home</Link>
              <span>/</span>
              <Link to="/solutions" className="hover:text-primary-red transition-colors">Solutions</Link>
              <span>/</span>
              <span className="text-charcoal">Financial Services</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Financial Services <span className="text-gradient">Solutions</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{animationDelay: '0.1s'}}>
              Empower your financial institution with cutting-edge AI and software solutions designed for banking, insurance, wealth management, and fintech innovation.
            </p>

            {/* Key Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 animate-slide-up" style={{animationDelay: '0.2s'}}>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm hover:shadow-md transition-all">
                <div className="text-3xl font-bold text-primary-red mb-1">98%</div>
                <div className="text-sm text-titanium">Fraud Detection Rate</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm hover:shadow-md transition-all">
                <div className="text-3xl font-bold text-primary-red mb-1">60%</div>
                <div className="text-sm text-titanium">Cost Reduction</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm hover:shadow-md transition-all">
                <div className="text-3xl font-bold text-primary-red mb-1">24/7</div>
                <div className="text-sm text-titanium">AI Customer Service</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-platinum shadow-sm hover:shadow-md transition-all">
                <div className="text-3xl font-bold text-primary-red mb-1">100%</div>
                <div className="text-sm text-titanium">Compliance Adherence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industry Challenges */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Navigating Financial Services Challenges
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Modern financial institutions face unprecedented complexity in operations, regulation, and customer expectations
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
                  title: 'Regulatory Compliance',
                  description: 'Navigate complex regulations including KYC, AML, GDPR, and industry-specific compliance requirements with automated monitoring and reporting.'
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />,
                  title: 'Fraud Prevention',
                  description: 'Combat sophisticated fraud schemes with AI-powered detection systems that identify anomalies in real-time and protect customer assets.'
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />,
                  title: 'Digital Transformation',
                  description: 'Modernize legacy systems and embrace digital banking while maintaining security and continuity of critical financial operations.'
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />,
                  title: 'Customer Experience',
                  description: 'Meet rising expectations for personalized, seamless digital experiences across all channels while maintaining human touch.'
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
                  title: 'Risk Management',
                  description: 'Implement comprehensive risk assessment and mitigation strategies powered by predictive analytics and real-time monitoring.'
                },
                {
                  icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />,
                  title: 'Operational Efficiency',
                  description: 'Streamline processes, reduce manual workload, and accelerate transaction processing while maintaining accuracy and security.'
                }
              ].map((challenge, index) => (
                <div key={index} className="bg-alabaster rounded-xl p-6 hover:shadow-lg transition-all duration-300 group">
                  <div className="w-14 h-14 bg-blush-light/40 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary-red/10 transition-colors">
                    <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {challenge.icon}
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-charcoal mb-3">{challenge.title}</h3>
                  <p className="text-titanium">{challenge.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Solutions */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Comprehensive Financial Technology Solutions
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                Purpose-built solutions for every segment of the financial services industry
              </p>
            </div>

            <div className="space-y-8">
              {/* Banking Solutions */}
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary-red to-deep-garnet rounded-2xl flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-charcoal mb-4">Digital Banking Platform</h3>
                    <p className="text-titanium mb-6">
                      Complete digital banking infrastructure with core banking systems, mobile banking apps, online portals, and omnichannel customer engagement platforms. Enable seamless account management, payments, lending, and wealth services.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Real-time transaction processing</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Multi-currency support</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">API-first architecture</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Advanced security protocols</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Insurance Tech */}
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-coral-mist to-primary-red rounded-2xl flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-charcoal mb-4">Insurance Technology (InsurTech)</h3>
                    <p className="text-titanium mb-6">
                      Modern insurance platforms with AI-powered underwriting, claims processing automation, risk assessment, and policy management. Accelerate decision-making and improve customer satisfaction with intelligent automation.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Automated claims processing</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">AI-powered risk scoring</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Digital policy administration</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Fraud detection systems</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Wealth Management */}
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-deep-garnet to-coral-mist rounded-2xl flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-charcoal mb-4">Wealth Management & Advisory</h3>
                    <p className="text-titanium mb-6">
                      Sophisticated portfolio management, robo-advisory platforms, financial planning tools, and investment analytics. Deliver personalized wealth management at scale with AI-driven insights and recommendations.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Portfolio optimization algorithms</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Automated rebalancing</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">ESG investment screening</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Client portal with reporting</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Processing */}
              <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary-red to-coral-mist rounded-2xl flex items-center justify-center">
                      <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-charcoal mb-4">Payment Processing & Fintech</h3>
                    <p className="text-titanium mb-6">
                      High-performance payment gateways, digital wallet solutions, peer-to-peer payment systems, and cryptocurrency platforms. Enable secure, fast, and compliant financial transactions across all channels.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Multi-channel payment acceptance</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">PCI DSS compliance</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Real-time fraud monitoring</span>
                      </div>
                      <div className="flex items-start">
                        <svg className="w-5 h-5 text-primary-red mr-3 mt-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-titanium">Blockchain integration</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI & Advanced Technologies */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-6">
                  AI-Powered <span className="text-gradient">Financial Intelligence</span>
                </h2>
                <p className="text-lg text-titanium mb-8">
                  Leverage cutting-edge artificial intelligence and machine learning to transform financial operations, enhance decision-making, and deliver superior customer experiences.
                </p>

                <div className="space-y-6">
                  {[
                    {
                      title: 'Intelligent Fraud Detection',
                      description: 'Advanced ML models analyze transaction patterns, user behavior, and network relationships to identify fraudulent activities in real-time with 98%+ accuracy.'
                    },
                    {
                      title: 'Credit Risk Assessment',
                      description: 'AI-driven credit scoring models that go beyond traditional metrics, incorporating alternative data sources for more accurate risk predictions.'
                    },
                    {
                      title: 'Algorithmic Trading',
                      description: 'High-frequency trading algorithms and automated portfolio management systems that execute optimal trades based on market conditions and risk parameters.'
                    },
                    {
                      title: 'Customer Behavior Analytics',
                      description: 'Predictive analytics to understand customer needs, personalize offerings, identify churn risks, and optimize cross-selling opportunities.'
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center mr-4 mt-1">
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-charcoal mb-2">{feature.title}</h3>
                        <p className="text-titanium">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-alabaster to-pearl-white rounded-2xl p-8 border border-platinum">
                  <div className="space-y-6">
                    {/* Tech Stack Visualization */}
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <h4 className="text-lg font-semibold text-charcoal mb-4">Core Technologies</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-blush-light/20 rounded-lg">
                          <div className="text-2xl font-bold text-primary-red mb-1">AI/ML</div>
                          <div className="text-xs text-titanium">TensorFlow, PyTorch</div>
                        </div>
                        <div className="text-center p-3 bg-blush-light/20 rounded-lg">
                          <div className="text-2xl font-bold text-primary-red mb-1">Blockchain</div>
                          <div className="text-xs text-titanium">Smart Contracts</div>
                        </div>
                        <div className="text-center p-3 bg-blush-light/20 rounded-lg">
                          <div className="text-2xl font-bold text-primary-red mb-1">Big Data</div>
                          <div className="text-xs text-titanium">Hadoop, Spark</div>
                        </div>
                        <div className="text-center p-3 bg-blush-light/20 rounded-lg">
                          <div className="text-2xl font-bold text-primary-red mb-1">Cloud</div>
                          <div className="text-xs text-titanium">AWS, Azure, GCP</div>
                        </div>
                      </div>
                    </div>

                    {/* Compliance Badges */}
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <h4 className="text-lg font-semibold text-charcoal mb-4">Compliance & Security</h4>
                      <div className="flex flex-wrap gap-3">
                        {['PCI DSS', 'SOC 2', 'ISO 27001', 'GDPR', 'KYC/AML', 'FINRA'].map((badge, i) => (
                          <span key={i} className="px-3 py-1 bg-primary-red/10 text-primary-red text-sm font-semibold rounded-full">
                            {badge}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Performance Metrics */}
                    <div className="bg-white rounded-xl p-6 shadow-sm">
                      <h4 className="text-lg font-semibold text-charcoal mb-4">Performance Impact</h4>
                      <div className="space-y-3">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-titanium">Processing Speed</span>
                            <span className="font-semibold text-charcoal">10x Faster</span>
                          </div>
                          <div className="w-full bg-platinum rounded-full h-2">
                            <div className="bg-primary-red rounded-full h-2" style={{width: '90%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-titanium">Cost Reduction</span>
                            <span className="font-semibold text-charcoal">60%</span>
                          </div>
                          <div className="w-full bg-platinum rounded-full h-2">
                            <div className="bg-primary-red rounded-full h-2" style={{width: '60%'}}></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-titanium">Customer Satisfaction</span>
                            <span className="font-semibold text-charcoal">95%</span>
                          </div>
                          <div className="w-full bg-platinum rounded-full h-2">
                            <div className="bg-primary-red rounded-full h-2" style={{width: '95%'}}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-alabaster to-white">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Real-World Success Stories
              </h2>
              <p className="text-xl text-titanium max-w-3xl mx-auto">
                See how leading financial institutions leverage our solutions to drive growth and innovation
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all">
                <div className="text-primary-red font-bold text-sm mb-2">RETAIL BANKING</div>
                <h3 className="text-xl font-bold text-charcoal mb-3">Digital-First Banking Transformation</h3>
                <p className="text-titanium mb-4">
                  Major retail bank deployed our digital banking platform, resulting in 3M+ digital customers acquired, 70% reduction in branch visits, and $50M annual cost savings.
                </p>
                <div className="flex items-center text-sm text-graphite">
                  <svg className="w-4 h-4 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  300% increase in digital transactions
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all">
                <div className="text-primary-red font-bold text-sm mb-2">INSURANCE</div>
                <h3 className="text-xl font-bold text-charcoal mb-3">AI-Powered Claims Automation</h3>
                <p className="text-titanium mb-4">
                  Leading insurer implemented our AI claims processing system, reducing claim settlement time from 30 days to 3 days with 95% straight-through processing.
                </p>
                <div className="flex items-center text-sm text-graphite">
                  <svg className="w-4 h-4 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  85% operational cost reduction
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all">
                <div className="text-primary-red font-bold text-sm mb-2">WEALTH MANAGEMENT</div>
                <h3 className="text-xl font-bold text-charcoal mb-3">Robo-Advisory Platform Launch</h3>
                <p className="text-titanium mb-4">
                  Investment firm launched robo-advisory service managing $2B+ in assets, serving 50K+ clients with personalized portfolios and automated rebalancing.
                </p>
                <div className="flex items-center text-sm text-graphite">
                  <svg className="w-4 h-4 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  40% higher portfolio returns
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Approach */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
                Our Implementation Approach
              </h2>
              <p className="text-xl text-titanium">
                Proven methodology ensuring successful deployment with minimal disruption
              </p>
            </div>

            <div className="space-y-4">
              {[
                {
                  phase: '01',
                  title: 'Discovery & Assessment',
                  description: 'Comprehensive analysis of current systems, processes, regulatory requirements, and business objectives to develop tailored implementation roadmap.'
                },
                {
                  phase: '02',
                  title: 'Solution Design',
                  description: 'Architecture design, technology selection, integration planning, and creation of detailed technical specifications aligned with business needs.'
                },
                {
                  phase: '03',
                  title: 'Development & Integration',
                  description: 'Agile development with continuous testing, seamless integration with existing systems, and rigorous security and compliance validation.'
                },
                {
                  phase: '04',
                  title: 'Testing & Validation',
                  description: 'Comprehensive testing including functional, security, performance, and user acceptance testing with regulatory compliance verification.'
                },
                {
                  phase: '05',
                  title: 'Deployment & Training',
                  description: 'Phased rollout strategy, comprehensive user training, change management support, and 24/7 go-live assistance.'
                },
                {
                  phase: '06',
                  title: 'Support & Optimization',
                  description: 'Ongoing monitoring, performance optimization, regular updates, and continuous improvement based on user feedback and emerging requirements.'
                }
              ].map((step, index) => (
                <div key={index} className="bg-alabaster rounded-xl p-6 hover:bg-white hover:shadow-lg transition-all duration-300 group">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mr-6">
                      <div className="w-12 h-12 bg-primary-red text-white rounded-lg flex items-center justify-center font-bold text-lg group-hover:scale-110 transition-transform">
                        {step.phase}
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-charcoal mb-2">{step.title}</h3>
                      <p className="text-titanium">{step.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-primary-red to-deep-garnet text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}></div>
        </div>

        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Transform Your Financial Services Operations
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Partner with us to implement cutting-edge technology solutions that drive growth, reduce costs, and enhance customer satisfaction in the financial services industry.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg hover:shadow-xl">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Solutions
              </Link>
            </div>
            <p className="mt-6 text-sm opacity-80">
              Trusted by 50+ financial institutions worldwide • 24/7 Enterprise Support Available
            </p>
          </div>
        </div>
      </section>

      {/* Related Solutions */}
      <section className="py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-charcoal mb-8">Related Industry Solutions</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/solutions/healthcare-life-sciences" className="card hover:shadow-xl transition-all group">
                <h3 className="text-lg font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">
                  Healthcare & Life Sciences
                </h3>
                <p className="text-sm text-titanium">AI-powered healthcare solutions for patient care and research</p>
              </Link>
              <Link to="/solutions/retail-ecommerce" className="card hover:shadow-xl transition-all group">
                <h3 className="text-lg font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">
                  Retail & E-commerce
                </h3>
                <p className="text-sm text-titanium">Digital commerce platforms and customer experience solutions</p>
              </Link>
              <Link to="/solutions/manufacturing-logistics" className="card hover:shadow-xl transition-all group">
                <h3 className="text-lg font-semibold text-charcoal mb-2 group-hover:text-primary-red transition-colors">
                  Manufacturing & Logistics
                </h3>
                <p className="text-sm text-titanium">Supply chain optimization and industrial automation</p>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FinancialServices;